import {useCurrentOrPublicUserContext} from "@/components/context/hooks.ts";
import Highcharts from "@/components/Highcharts";
import {CheckDetailsAlert, LocationResponseTimes} from "@/lib/queries/checks.ts";
import {StandardDateRange} from "@/lib/queries/reports.ts";
import {MonitoringGroup} from "@/lib/types/monitoring.ts";
import {formatResponseTime} from "@/pages/checks/analysis/overview/utils.ts";
import {differenceInHours, formatISO, parseISO} from "date-fns";
import {formatInTimeZone} from "date-fns-tz";
import {enGB} from "date-fns/locale/en-GB";
import {Series, SeriesOptionsType} from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {SeriesLegendItemClickEventObject} from "highcharts/highcharts";
import {useEffect, useRef, useState} from "react";
import {useDebouncedCallback} from "use-debounce";

const ALERT_COLOR = "rgba(206, 5, 0, 0.3)";

const GAP_THRESHOLD_PCT = 0.12;

interface ResponseTimeAreaChartProps {
  start: Date;
  end: Date;
  responseTimes: LocationResponseTimes[];
  locations: Pick<MonitoringGroup, "id" | "location">[];
  alerts: CheckDetailsAlert[];
  onLocationClick: (id: number, visible: boolean) => void;
  onRangeChange: (start: Date | null, end: Date | null, fixedRange: StandardDateRange | null) => void;
  showMarkers?: boolean;
  showPlotbandsInLegend?: boolean;
  /** Overrides the viewer's timezone — status pages label their axes in the page's own. */
  timezone?: string;
}

interface customSeriesLegendItemClickEventObject extends SeriesLegendItemClickEventObject {
  target: Series & {options: SeriesOptionsType & {locationId?: number; id: number}};
}

export const ResponseTimeAreaChart = ({
  start,
  end,
  responseTimes,
  alerts,
  locations,
  onLocationClick,
  onRangeChange,
  showMarkers = true,
  showPlotbandsInLegend = false,
  timezone: timezoneProp,
}: ResponseTimeAreaChartProps) => {
  const [plotBandsVisible, setPlotBandsVisible] = useState(true);
  const {timezone: viewerTimezone, getShortDateTime} = useCurrentOrPublicUserContext();
  const timezone = timezoneProp ?? viewerTimezone;
  const isDaily = differenceInHours(end, start) <= 24 * 7;
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const debouncedRangeChange = useDebouncedCallback(
    (min: number, max: number) => onRangeChange(new Date(min), new Date(max), null),
    300
  );

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (chart?.xAxis?.[0]) {
      chart.xAxis[0].setExtremes(start.getTime(), end.getTime(), true, false);
    }
  }, [start, end]);

  const plotBands = alerts
    .filter((a) => !a.state_is_up)
    .map((alert) => ({
      color: ALERT_COLOR,
      from: parseISO(alert.created_at).getTime(),
      to: alert.ended_at ? parseISO(alert.ended_at).getTime() : end.getTime(),
    }));

  let minX = start.getTime();
  let maxX = end.getTime();
  let maxY = 0;

  responseTimes.forEach((responses) => {
    responses.data.forEach((item) => {
      const time = parseISO(item.timestamp);
      if (maxY < item.value) {
        maxY = item.value;
      }
      if (maxX < time.getTime()) {
        maxX = time.getTime();
      }
      if (minX > time.getTime()) {
        minX = time.getTime();
      }
    });
  });
  if (maxY === 0) {
    maxY = 1.0;
  }
  if (maxX === 0) {
    maxX = new Date().getTime();
  }
  if (minX === 0) {
    minX = start.getTime();
  }

  const series = responseTimes.map((responses, idx) => ({
    type: "area",
    opacity: 0.4,
    id: idx,
    locationId: responses.id,
    name: locations.find((l) => l.id === responses.id)?.location,
    tooltip: {
      headerFormat: "",
      pointFormatter: function (): string {
        // @ts-expect-error @typescript-eslint/no-this-alias
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const params: {
          x: number;
          y: number;
          color: string;
          series: {
            index: number;
            name: string;
          };
        } = this;
        const date = formatISO(new Date(params.x));
        const value = params.y;
        const name = params.series.name;
        const format = `<span style="color:${params.color}">●</span> ${name}: <b>${formatResponseTime(value)}</b><br/>`;
        const formattedDate = getShortDateTime(date);
        return `<span style="font-size: 12px;margin-bottom: 4px">${formattedDate}</span><br/>${format}`;
      },
    },
    data: responses.data.map((p) => [parseISO(p.timestamp).getTime(), p.value]),
  }));

  const isEmpty = series.length === 0;
  const options = {
    title: {text: ""},
    credits: {
      enabled: false,
    },
    chart: {
      zooming: {
        type: "x",
        resetButton: {
          theme: {
            style: {
              display: "none",
            },
          },
        },
      },
      time: {
        timezone: timezone,
      },
    },
    plotOptions: {
      series: {
        events: {
          legendItemClick: (e: customSeriesLegendItemClickEventObject) => {
            if (!e.target.options.id && e.target.options.id !== 0) {
              if (e.target.name === "Outage Detected") {
                setPlotBandsVisible(!plotBandsVisible);
                return false;
              }
              return;
            }
            if (e.target.options.locationId) {
              onLocationClick(e.target.options.locationId, e.target.visible);
            }
          },
        },
      },
      area: {
        marker: {
          enabled: showMarkers,
        },
        gapSize: GAP_THRESHOLD_PCT * (maxX - minX),
        gapUnit: "value",
      },
    },
    tooltip: {
      shared: true,
      outside: true,
    },
    xAxis: {
      type: "datetime",
      gridLineWidth: 1,
      plotBands: plotBandsVisible ? plotBands : [],
      events: {
        setExtremes: (e: Highcharts.AxisSetExtremesEventObject) => {
          if (e.trigger !== "zoom") {
            return;
          }
          if (e.min && e.max) {
            debouncedRangeChange(e.min, e.max);
            return;
          }

          onRangeChange(null, null, null);
        },
      },
      labels: {
        formatter: ({value}: {value: number}) => {
          if (isDaily) {
            return formatInTimeZone(new Date(value), timezone, "ha z", {locale: enGB});
          }
          return formatInTimeZone(new Date(value), timezone, "MMM dd", {locale: enGB});
        },
      },
      min: minX,
      max: maxX,
    },
    yAxis: {
      title: {
        text: undefined,
      },
      labels: {
        formatter: ({value}: {value: number}) => {
          if (!value) {
            return 0;
          }
          return formatResponseTime(value);
        },
      },
      min: 0,
      max: maxY,
    },
    series: isEmpty
      ? [{name: "No data available"}]
      : [
          ...series,
          ...(showPlotbandsInLegend && plotBands.length > 0
            ? [
                {
                  name: "Outage Detected",
                  color: ALERT_COLOR,
                  lineWidth: 0,
                  marker: {
                    symbol: "square",
                  },
                },
              ]
            : []),
        ],
  };
  return <HighchartsReact ref={chartRef} highcharts={Highcharts} options={options} />;
};
